"use client";

/**
 * Debounced per-candidate per-step override autosave (spec §7,
 * §16.16 stale-write guard).
 *
 * The override editor calls `save(payload)` on every change; we wait
 * 800ms (configurable) before firing the PATCH so a recruiter typing
 * a long body doesn't generate a PATCH per keystroke. Each call
 * stamps a monotonically-increasing `request_seq` that the backend
 * echoes — `applyResponse` upstream drops out-of-order responses.
 *
 * The hook returns:
 *   - `save(payload)`: schedule the save (debounced).
 *   - `flush()`: fire any pending payload immediately (used on blur or
 *     before navigating away).
 *   - `cancel()`: drop any pending payload (used on a "Reset to
 *     default" click which already wipes the override server-side).
 *   - `status`: `'idle' | 'saving' | 'saved' | 'error'` for the auto-
 *     save indicator.
 *
 * The hook does NOT debug-bind to the DOM — the editor decides when
 * to call `save`. That keeps autosave logic UI-agnostic so it works
 * for textarea, TipTap, and chip-input editors alike.
 */

import * as React from "react";

import { showToast } from "@/components/ui/sonner";
import { getApiErrorMessage } from "@/lib/api/error";

import type {
  EnrollmentPreviewPatchRequestSetOverrides,
  StepOverridePayload,
} from "../lib/types";

export type AutosaveStatus = "idle" | "saving" | "saved" | "error";

/**
 * Registry of every mounted hook instance's `flush`. The preview sheet is
 * a singleton surface, so a module-level set is sufficient. Commit MUST
 * drain this first — otherwise an edit made <`delayMs` before clicking
 * Enroll would never reach the backend and the frozen content would miss
 * the recruiter's last keystrokes.
 */
const flushRegistry = new Set<() => Promise<void>>();

/** Fire every pending debounced override save and wait for them all.
 * Failures are swallowed — the commit path re-validates server-side and
 * the per-card status badge already surfaces save errors. */
export async function flushAllPendingOverrides(): Promise<void> {
  await Promise.all(
    [...flushRegistry].map((flush) => flush().catch(() => undefined)),
  );
}

export interface UseDebouncedOverrideArgs {
  jobCandidateId: string;
  stepId: string;
  /** Underlying mutator — the preview shell binds this to
   * `useEnrollmentPreview.patchPreview`. */
  patchPreview: (data: {
    set_overrides?: EnrollmentPreviewPatchRequestSetOverrides;
    request_seq?: number | null;
  }) => Promise<unknown>;
  /** ms to wait after the last `save` call before flushing. */
  delayMs?: number;
}

export interface UseDebouncedOverrideResult {
  save: (payload: StepOverridePayload) => void;
  flush: () => Promise<void>;
  cancel: () => void;
  status: AutosaveStatus;
  /** "Saved a moment ago" badges can read this to render relative copy. */
  lastSavedAt: number | null;
}

export function useDebouncedOverride(
  args: UseDebouncedOverrideArgs,
): UseDebouncedOverrideResult {
  const { jobCandidateId, stepId, patchPreview, delayMs = 800 } = args;
  const [status, setStatus] = React.useState<AutosaveStatus>("idle");
  const [lastSavedAt, setLastSavedAt] = React.useState<number | null>(null);

  // Pending payload is held in a ref so consecutive `save` calls
  // overwrite without triggering a re-render.
  const pendingPayloadRef = React.useRef<StepOverridePayload | null>(null);
  const seqRef = React.useRef(0);
  const lastAppliedSeqRef = React.useRef(0);
  const timerRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  const unmountedRef = React.useRef(false);

  const fire = React.useCallback(async () => {
    const payload = pendingPayloadRef.current;
    if (!payload) return;
    pendingPayloadRef.current = null;
    const seq = ++seqRef.current;
    if (!unmountedRef.current) setStatus("saving");
    try {
      await patchPreview({
        set_overrides: { [jobCandidateId]: { [stepId]: payload } },
        request_seq: seq,
      });
      // Drop stale responses — `request_seq` strictly increases.
      if (seq < lastAppliedSeqRef.current) return;
      lastAppliedSeqRef.current = seq;
      if (unmountedRef.current) return;
      setStatus("saved");
      setLastSavedAt(Date.now());
    } catch (err) {
      if (!unmountedRef.current) setStatus("error");
      showToast.error("Couldn't save this override. Please try again.", {
        description: getApiErrorMessage(err),
      });
    }
  }, [patchPreview, jobCandidateId, stepId]);

  const save = React.useCallback(
    (payload: StepOverridePayload) => {
      pendingPayloadRef.current = payload;
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => {
        timerRef.current = null;
        void fire();
      }, delayMs);
    },
    [fire, delayMs],
  );

  const flush = React.useCallback(async () => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    if (!pendingPayloadRef.current) return;
    await fire();
  }, [fire]);

  const cancel = React.useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    pendingPayloadRef.current = null;
  }, []);

  // FLUSH any pending payload when the hook unmounts. The step cards are
  // keyed per candidate, so switching candidates unmounts the card — an
  // edit made inside the debounce window MUST still reach the backend or
  // it silently reverts when the recruiter switches back (the card
  // re-seeds from the server-rendered cell). `patchPreview` is bound at
  // the sheet level and outlives the card, so firing during unmount
  // completes normally; we skip the post-await setState since the
  // component is gone.
  React.useEffect(() => {
    unmountedRef.current = false;
    return () => {
      unmountedRef.current = true;
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
      void fire();
    };
    // `fire` is stable for a given (candidate, step, patchPreview) —
    // the card remounts on identity changes.
  }, [fire]);

  // Register this instance's flush so the commit path can drain every
  // pending autosave before POSTing (see `flushAllPendingOverrides`).
  React.useEffect(() => {
    flushRegistry.add(flush);
    return () => {
      flushRegistry.delete(flush);
    };
  }, [flush]);

  return { save, flush, cancel, status, lastSavedAt };
}
